import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Landmark, Wallet, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import { MobileCashRegisterForm } from '../../component/cashregister/MobileCashRegisters';
import useIsMobileShell from '../../hooks/useIsMobileShell';
import { settingRowSx, useStandaloneFieldSx } from './shared';

// Formulário de caixa — DESIGN-SYSTEM.md §5.6, drawer lateral (mesmo padrão
// já usado por `pages/categories/Form.js`: `drawer-overlay`/`drawer-box`
// viram o `Drawer` do MUI aberto por `pages/cashregisters/List.js`,
// `drawer-head`/`drawer-body`/`drawer-foot` viram os 3 `Box` abaixo).
// Não há protótipo de página cheia dedicado (mesma decisão de Categoria) —
// acessar `/cashregisters/new`/`/cashregisters/edit/:id` diretamente (fora do
// drawer) redireciona para `/cashregisters`, onde o mesmo formulário roda
// embutido no drawer da Lista.
//
// Campos reais persistidos via `POST/PUT /cashregister` (`cashregister/
// creator.go`/`updater.go`): `name` e `allows_bleeding` ("Permitir sangria").
// `default` (o antigo "Caixa Padrão", que centraliza lançamentos financeiros)
// não tem mais editor nesta tela — o novo protótipo não pede esse campo —,
// mas o valor já existente de um caixa em edição é preservado (buscado no
// GET e reenviado sem alteração no PUT) para não desativar silenciosamente
// um caixa padrão já configurado.
//
// Lacuna de backend documentada (sem inventar dado/endpoint — mesma
// convenção das tarefas anteriores desta sessão): "Caixa ativo" não tem
// coluna nenhuma no schema de `cash_registers` que aceite esse valor como
// entrada hoje (a coluna `active` existe, mas `Updater.Update`/
// `Creator.Create` sempre gravam `true` fixo, nunca aceitam esse valor como
// entrada — não há como desativar um caixa pela API). O campo abaixo
// continua na UI (o design system exige a casca visual completa), mas só
// vive no estado em memória da Lista de caixas (`onSuccess` devolve esse
// valor para a Lista mesclar no registro local, o mesmo padrão que o
// próprio protótipo usa com seu array `CASHREGISTERS` em memória) — não
// sobrevive a um reload da página.
//
// Correção de bug: "Local / observação" e "Integração automática" foram
// removidos desta tela — não existe coluna nenhuma para "local" no schema, e
// o caixa não se conecta com nenhuma maquininha (não há sincronização
// automática real; o único dado real de integração é `code_integration`,
// exibido/copiável em `components/IntegrationModal.js`).
export default function Form({
    embedded = false,
    cashRegisterId,
    initialOverrides,
    onClose: onCloseProp,
    onSuccess: onSuccessProp,
    onError: onErrorProp,
}) {
    const navigate = useNavigate();
    const routeParams = useParams();
    const standaloneFieldSx = useStandaloneFieldSx();
    const isMobileShell = useIsMobileShell();
    // Shell mobile: as rotas `/cashregisters/new|edit/:id` abrem o formulário em página cheia
    // (`caixas/form.html`); no desktop continua sendo o drawer da Lista.
    const mobilePage = !embedded && isMobileShell;
    const [snack, setSnack] = useState({ open: false, message: '' });
    const onClose = mobilePage ? () => navigate('/cashregisters') : onCloseProp;
    const onSuccess = mobilePage
        ? (updated) => navigate('/cashregisters', { replace: true, state: { toast: updated?.id ? 'Caixa atualizado com sucesso!' : 'Caixa cadastrado com sucesso!' } })
        : onSuccessProp;
    const onError = mobilePage
        ? (responseData) => setSnack({
            open: true,
            message: typeof responseData === 'string' ? responseData : (responseData?.message || responseData?.error || 'Erro ao salvar caixa'),
        })
        : onErrorProp;
    useAppBarTitle(mobilePage && routeParams.id ? 'Editar caixa' : '');

    useEffect(() => {
        if (!embedded && !isMobileShell) {
            navigate('/cashregisters', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embedded, isMobileShell]);

    const id = mobilePage ? routeParams.id : cashRegisterId;
    const isEdit = Boolean(id);

    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const [active, setActive] = useState(true);
    const [allowsBleeding, setAllowsBleeding] = useState(true);
    const [defaultCash, setDefaultCash] = useState(false);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const resetForm = () => {
        setName('');
        setNameError('');
        setActive(true);
        setAllowsBleeding(true);
        setDefaultCash(false);
    };

    const fetchCashRegister = async (cashRegisterIdToFetch) => {
        setLoading(true);
        try {
            const response = await httpClient.get(`/cashregister/${cashRegisterIdToFetch}`);
            const data = response.data || {};
            setName(data.name || '');
            setAllowsBleeding(Boolean(data.allows_bleeding));
            setDefaultCash(Boolean(data.default));
            // "Caixa ativo" não existe na API (ver nota acima) — vem só do
            // que a Lista já tinha em memória para este caixa
            // (`initialOverrides`).
            setActive(initialOverrides?.active !== false);
        } catch (error) {
            console.error('Erro ao carregar caixa:', error);
            onError?.(error.response?.data || error.message || 'Erro ao carregar caixa');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchCashRegister(id);
        } else {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleSave = async () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setNameError('Informe o nome do caixa.');
            return;
        }
        setNameError('');
        setSaving(true);
        try {
            const overrides = { active };
            if (isEdit) {
                // `httpClient` nunca rejeita por status HTTP (ver
                // `common/httpclient.js`), então o status é checado à mão
                // (mesmo padrão de `pages/categories/Form.js`).
                const response = await httpClient.put(`/cashregister/${id}`, {
                    name: trimmedName,
                    allows_bleeding: allowsBleeding,
                    default: defaultCash,
                });
                if (response.status === 204) {
                    onSuccess?.({ id, name: trimmedName, allows_bleeding: allowsBleeding, ...overrides });
                } else {
                    onError?.(response.data);
                }
            } else {
                // `cashregister.Creator`/`InputCashRegister` não tem tags
                // `json:"..."` nos campos (só `CashRegisterUpdateInput`, usado
                // pelo PUT, tem) — o decode no backend casa por nome
                // case-insensitive, então as chaves precisam ser PascalCase
                // (`Name`/`AllowsBleeding`), não snake_case. Além disso
                // `Repository.Create`/`SaveCashRegister` sempre gera um UUID
                // novo no servidor e ignora qualquer `ID` enviado pelo
                // cliente — mas o handler `Creator.Create` responde com
                // `{"id": <o ID que o cliente mandou>}`, não com o UUID
                // real gravado (achado de backend, não corrigido aqui por
                // estar fora do escopo desta tarefa). Por isso o `onSuccess`
                // não devolve nenhum `id` sintético — `List.js` sempre
                // recarrega a lista inteira após criar, em vez de inserir
                // otimisticamente uma linha com um id que não bateria com o
                // registro real no banco.
                const response = await httpClient.post('/cashregister', {
                    Name: trimmedName,
                    AllowsBleeding: allowsBleeding,
                    Default: false,
                    CreatedDate: new Date().toISOString(),
                });
                if (response.status === 201) {
                    onSuccess?.({ name: trimmedName, allows_bleeding: allowsBleeding, status: 'closed', ...overrides });
                } else {
                    onError?.(response.data);
                }
            }
        } catch (error) {
            console.error('Erro ao salvar caixa:', error);
            onError?.(error.response?.data || error.message || 'Erro ao salvar caixa');
        } finally {
            setSaving(false);
        }
    };

    if (mobilePage) {
        return (
            <PageScaffold
                links={[]}
                openSnackbar={snack.open}
                setOpenSnackbar={(open) => setSnack((prev) => ({ ...prev, open }))}
                message={snack.message}
                severity="error"
            >
                <MobileCashRegisterForm
                    name={name}
                    onNameChange={(value) => { setName(value); if (nameError) setNameError(''); }}
                    nameError={nameError}
                    active={active}
                    onActiveChange={setActive}
                    allowsBleeding={allowsBleeding}
                    onAllowsBleedingChange={setAllowsBleeding}
                    loading={loading}
                    saving={saving}
                    onCancel={() => onClose?.()}
                    onSave={handleSave}
                />
            </PageScaffold>
        );
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'background.default' }}>
            {/* drawer-head */}
            <Box sx={{ p: '22px 22px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mb: '10px',
                        color: '#fff',
                        background: (t) => t.palette.gradient.brand,
                    }}>
                        <Wallet size={18} strokeWidth={2} />
                    </Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, mb: '4px' }}>
                        {isEdit ? 'Editar caixa' : 'Novo caixa'}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                        {isEdit ? `Editando "${name || ''}".` : 'Cadastre um novo terminal de caixa.'}
                    </Typography>
                </Box>
                <IconButton onClick={() => onClose?.()} size="small" aria-label="Fechar">
                    <X size={18} strokeWidth={2} />
                </IconButton>
            </Box>

            {/* drawer-body */}
            <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                        <CircularProgress size={36} thickness={4} />
                    </Box>
                ) : (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <TextField
                                autoFocus
                                fullWidth
                                label="Nome do caixa *"
                                value={name}
                                onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
                                error={Boolean(nameError)}
                                helperText={nameError}
                                variant="outlined"
                                inputProps={{ maxLength: 60 }}
                                sx={standaloneFieldSx}
                            />
                        </Box>
                        <Box sx={settingRowSx(false)}>
                            <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'background.subtle', color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Wallet size={16} strokeWidth={2} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Caixa ativo</Typography>
                                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                    Caixas inativos ficam ocultos na abertura de caixa do PDV
                                </Typography>
                            </Box>
                            <Switch checked={active} onChange={(e) => setActive(e.target.checked)} />
                        </Box>

                        <Box sx={settingRowSx(true)}>
                            <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'background.subtle', color: 'text.secondary', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Landmark size={16} strokeWidth={2} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Permitir sangria</Typography>
                                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                    Libera o registro de sangrias enquanto este caixa estiver aberto
                                </Typography>
                            </Box>
                            <Switch checked={allowsBleeding} onChange={(e) => setAllowsBleeding(e.target.checked)} />
                        </Box>
                    </>
                )}
            </Box>

            {/* drawer-foot */}
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0 }}>
                <Button variant="outlined" onClick={() => onClose?.()} size="small" disabled={saving}>
                    Cancelar
                </Button>
                <Button variant="contained" onClick={handleSave} size="small" disabled={saving || loading}>
                    {saving ? 'Salvando...' : 'Salvar caixa'}
                </Button>
            </Box>
        </Box>
    );
}
